import { EmailTemplate } from '@spike/email-marketing-model';
import clsx from 'clsx';
import EmailTemplatePreviewModal from 'components/EmailMarketing/EmailTemplates/Gallery/EmailTemplatePreviewModal';
import EmailTemplatesGallery from 'components/EmailMarketing/EmailTemplates/Gallery/EmailTemplatesGallery';
import { FunctionComponent, useState } from 'react';
import SavedTemplatesList from './SavedTemplatesList';
import SelectedTemplatePreview from './SelectedTemplatePreview';
import styles from './TemplateStepPicker.module.scss';

type PickerTab = 'saved' | 'gallery';

interface TemplateStepPickerProps {
    selectedTemplate: EmailTemplate | null;
    onSelectTemplate: (template: EmailTemplate) => void;
    onClearTemplate: () => void;
    onStartFromScratch: () => void;
    onEditTemplate: (template: EmailTemplate) => void;
    /**
     * True while the parent is cloning a system template into the user's
     * library ahead of opening the editor. Forwarded to the preview so the
     * Edit action reads as "Duplicating…" and is temporarily disabled.
     */
    cloningTemplate?: boolean;
}

/**
 * Right column of step 2. Three visual states:
 *
 *   1. No template selected, "Email Saved" tab active (default):
 *        - shows the user's own templates list
 *   2. No template selected, "Template Gallery" tab active:
 *        - shows the curated system gallery in picker mode
 *   3. A template is selected:
 *        - the tab bar disappears; we show the preview of the selected template
 *          with a "Change Template" link to go back to picking
 *
 * The label "Templates" stays visible in all three states as a section header.
 */
const TemplateStepPicker: FunctionComponent<TemplateStepPickerProps> = ({
    selectedTemplate,
    onSelectTemplate,
    onClearTemplate,
    onStartFromScratch,
    onEditTemplate,
    cloningTemplate = false
}) => {
    const [activeTab, setActiveTab] = useState<PickerTab>('saved');
    const [previewTemplate, setPreviewTemplate] = useState<EmailTemplate | null>(null);

    const handleEdit = () => {
        if (selectedTemplate) {
            onEditTemplate(selectedTemplate);
        }
    };

    // System templates can't be edited in place — the BE rejects updates on
    // them. The parent handles the clone-and-edit flow, but the button label
    // needs to tell the user upfront that the click will duplicate first.
    const editLabel = selectedTemplate?.editable === false ? 'Duplicate & Edit' : 'Edit';

    return (
        <div className={styles.root}>
            <div className={styles.header}>
                <span className={styles.sectionLabel}>Templates</span>

                {!selectedTemplate && (
                    <div className={styles.tabBar}>
                        <button
                            type="button"
                            className={clsx(styles.tab, { [styles.tabActive]: activeTab === 'saved' })}
                            onClick={() => setActiveTab('saved')}
                        >
                            Email Saved
                        </button>
                        <button
                            type="button"
                            className={clsx(styles.tab, { [styles.tabActive]: activeTab === 'gallery' })}
                            onClick={() => setActiveTab('gallery')}
                        >
                            Template Gallery
                        </button>
                    </div>
                )}
            </div>

            <div className={styles.content}>
                {selectedTemplate ? (
                    <SelectedTemplatePreview
                        template={selectedTemplate}
                        onEditTemplate={handleEdit}
                        editLabel={editLabel}
                        editDisabled={cloningTemplate}
                        onChangeTemplate={onClearTemplate}
                    />
                ) : activeTab === 'saved' ? (
                    <SavedTemplatesList
                        onSelectTemplate={onSelectTemplate}
                        onStartFromScratch={onStartFromScratch}
                    />
                ) : (
                    <EmailTemplatesGallery
                        pickerMode
                        onSelectTemplate={onSelectTemplate}
                        onPreviewTemplate={setPreviewTemplate}
                    />
                )}
            </div>

            <EmailTemplatePreviewModal
                template={previewTemplate}
                onClose={() => setPreviewTemplate(null)}
            />
        </div>
    );
};

export default TemplateStepPicker;
